import {Component, EventEmitter, OnInit, Output} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {SigningProcessService} from '../../_services/signing-process.service';
import {SigningProcess} from '../../_models/signing-process.model';
import {BehaviorSubject, Subject, switchMap, takeUntil, timer} from 'rxjs';
import {UIService} from '../../_services/ui.service';
import {IdentityService} from '../../_services/identity.service';
import {SigningProcessStepService} from '../../_services/signing-process-steps.service';
import {SigningProcessStep} from '../../_enums/signing-process-step.enum';
import {ErrorPageService} from '../../_services/error-page.service';
import {RestApiService} from '../../_services/rest-api.service';
import {Step} from '../../_enums/step.enum';
import {LogType} from '../../_enums/log-type.enum';
import {DialogService} from '../../_services/dialog.service';
import {DialogComponent} from '../dialog/dialog.component';
import {DomSanitizer} from '@angular/platform-browser';
import {InternalErrorCode} from '../../_enums/internal-error-code.enum';
import { ErrorMessage } from '../../_enums/error-message.enum';

@Component({
	selector: 'x-face-compare-polling',
	templateUrl: './face-compare-polling.component.html',
	styleUrl: './face-compare-polling.component.css',
})
export class FaceComparePollingComponent implements OnInit {
	private signingProcess: SigningProcess = new SigningProcess();
	@Output() changeStepperState: EventEmitter<undefined> = new EventEmitter<undefined>();
	public loading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
	private stopPolling$ = new Subject<void>();
	public selfieUnsuccessfully: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

	constructor(
		private route: ActivatedRoute,
		private signingProcessService: SigningProcessService,
		private readonly uIService: UIService,
		private identityService: IdentityService,
		private readonly signingProcessStepService: SigningProcessStepService,
		private errorPageService: ErrorPageService,
		private restApiService: RestApiService,
		private dialogService: DialogService,
		private sanitizer: DomSanitizer
	) {}
	ngOnInit(): void {
		const biometryId = this.getBiometryIdFromUrl();
		if (!biometryId) {
			this.errorPageService.setError(null, ErrorMessage.NoBiometryFromUrlError, InternalErrorCode.NoBiometryFromUrlError);
		} else {
			const sessionData = sessionStorage.getItem(biometryId);
			if (!sessionData) {
				this.errorPageService.setError(null, ErrorMessage.SessionDataError, InternalErrorCode.SessionDataError);
			} else {
				try {
					const parsedData = JSON.parse(sessionData);
					this.setSigningProcessData(parsedData);
					this.startBiometryPolling(biometryId, parsedData.authId, this.getStageVersionFromUrl());
				} catch (e) {
					this.uIService.responseLog('Error parsing sessionData:', e);
					this.errorPageService.setError(null, ErrorMessage.SessionDataError, InternalErrorCode.SessionDataError);
				}
			}
		}
	}

	private getBiometryIdFromUrl(): string | null {
		return this.route.snapshot.queryParamMap.get('biometryId');
	}

	/**
	 * El stage del intento de FLD que completó el cliente, tal como lo firma Biometry en la URL del callback.
	 * Sin él (o con basura) no se manda nada e IS resuelve el stage más nuevo, como hasta hoy.
	 * Number(null) y Number('') valen 0: solo un entero sin signo cuenta como stage.
	 */
	private getStageVersionFromUrl(): number | undefined {
		const raw = this.route.snapshot.queryParamMap.get('stageVersion');
		return raw !== null && /^\d+$/.test(raw) ? Number(raw) : undefined;
	}

	private setSigningProcessData(data: any) {
		this.signingProcess.authId = data.authId;
		this.signingProcess.metodo_retiro = data.metodo_retiro;
		this.signingProcess.monto_cuota = data.monto_cuota;
		this.signingProcess.capital = data.capital;
		this.signingProcess.cantidad_cuotas = data.cantidad_cuotas;
		this.signingProcess.validationId = data.validationId;
		this.signingProcess.planId = data.planId;
		this.signingProcess.creditFlowId = data.creditFlowId;
		this.signingProcess.needsPep = data.needsPep;
		this.signingProcessService.setSigningProcess(this.signingProcess);
	}

	private startBiometryPolling(biometryId: string, authId?: number, stageVersion?: number) {
		this.restApiService
			.monitoringLog({creditFlowId: this.signingProcess.creditFlowId, step: Step.FLDCallback, logType: LogType.Flujo})
			.subscribe();
		timer(0, 10000)
			.pipe(
				switchMap(() => this.identityService.verifyFaceCompare(biometryId, authId, stageVersion)),
				takeUntil(this.stopPolling$)
			)
			.subscribe({
				next: (data: any) => {
					if (data && typeof data.renaperStatus === 'boolean') {
						this.stopPolling();
						if (data.renaperStatus) {
							this.restApiService
								.monitoringLog({creditFlowId: this.signingProcess.creditFlowId, step: Step.NeutralSelfie, logType: LogType.Flujo})
								.subscribe();
							this.uIService.successMessage('Exitoso!', 'Validación de foto exitosa.');
							this.changeStepperState.emit();
							if (this.signingProcess.needsPep) {
								this.signingProcessStepService.moveTo(SigningProcessStep.PepForm);
							} else {
								this.signingProcessStepService.moveTo(SigningProcessStep.SignaturePad);
							}
						} else {
							this.showErrorSelfieUpload();
							this.restApiService
								.monitoringLog({
									creditFlowId: this.signingProcess.creditFlowId,
									step: Step.NeutralSelfie,
									logType: LogType.Consola,
									error: data,
								})
								.subscribe();
						}
					} else {
						console.log('Biometry still processing...');
					}
				},
				error: (err) => {
					this.stopPolling();
					this.restApiService
						.monitoringLog({
							creditFlowId: this.signingProcess.creditFlowId,
							step: Step.NeutralSelfie,
							logType: LogType.Consola,
							error: err,
						})
						.subscribe();
					if (err.status === 422) {
						this.uIService.responseLog('Biometría inválida.',err);
						this.errorPageService.setError('Biometría fallida', err.error.message, err.error.code, null, err.error.errorId);
					} else if (err.error.code == InternalErrorCode.UserBlockedError) {
						this.errorPageService.setError(err.error.message ?? null, null, err.error.code, null, err.error.errorId);
					} else {
						this.uIService.responseLog('Polling error (subscribe)', err);
						this.errorPageService.setError(null, err.error.message ?? null, err.error.code, null, err.error.errorId);
					}
				},
			});
	}
	public next(): void {
		if (!this.selfieUnsuccessfully.getValue()) {
			this.changeStepperState.emit();
			if (this.signingProcess.needsPep) {
				this.signingProcessStepService.moveTo(SigningProcessStep.PepForm);
			} else {
				this.signingProcessStepService.moveTo(SigningProcessStep.SignaturePad);
			}
		} else {
			this.restApiService
				.monitoringLog({
					creditFlowId: this.signingProcess.creditFlowId,
					step: Step.NeutralSelfie,
					logType: LogType.Consola,
					error: 'Los rostros de la persona no coinciden',
				})
				.subscribe();
			this.uIService.errorMessage('Error!', 'Los rostros de la persona no coinciden');
		}
	}
	private stopPolling() {
		this.stopPolling$.next();
		this.stopPolling$.complete();
		this.loading.next(false);
	}
	public showErrorSelfieUpload() {
		const dialogRef = this.dialogService.openDialog(DialogComponent, {
			title: 'Los rostros no coinciden',
			data: this.sanitizer.bypassSecurityTrustHtml(`No pudimos validar tu rostro, por favor intentá nuevamente.`),
			buttonLabel: 'Reintentar',
		});
	}
}
